import { Calendar } from '@seek/date-picker';
import { Box, IconDate, Inline, Text } from 'braid-design-system';
import React, {
  type FunctionComponent,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useConfig } from 'src/app/shared/hooks/useConfig';
import { formatDateText, setUtcDateTime } from 'src/app/utils/date';

import * as styles from './index.css';

interface Props {
  max: string;
  end: string;
  now: string;
  endDate: Date;
  handleDateClick: (utcDate: string) => void;
}

export const AdExpiryDateInput: FunctionComponent<Props> = ({
  max,
  end,
  now,
  endDate,
  handleDateClick,
}) => {
  const [selectedMonthDate, setSelectedMonthDate] = useState(endDate);
  const [showCalendar, setShowCalendar] = useState(false);
  const wrapperRef: RefObject<any> = useRef();
  const { getLocale } = useConfig();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent | TouchEvent) {
      if (!wrapperRef.current?.contains(event.target)) {
        hideCalendar();
      }
    }

    function handleEscKeypress(event: KeyboardEvent) {
      if ((event.key === 'Esc' || event.key === 'Escape') && showCalendar) {
        hideCalendar();
      }
    }

    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('touchend', handleClickOutside, true);
    document.addEventListener('keydown', handleEscKeypress, false);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('touchend', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscKeypress, false);
    };
  });

  const hideCalendar = () => setShowCalendar(false);

  const onDateClick = (date: string) => {
    const utcDateTimeStr = setUtcDateTime(date);
    handleDateClick(utcDateTimeStr);
    hideCalendar();
  };

  const onMonthChange = (date: string) => {
    setSelectedMonthDate(new Date(date));
  };

  const onDateTriggerClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setShowCalendar(!showCalendar);
  };

  return (
    <Box position="relative">
      <Box
        id="AdExpiryDateTigger"
        borderRadius="standard"
        boxShadow="borderNeutralLight"
        className={styles.trigger}
        component="button"
        cursor="pointer"
        display="block"
        padding="small"
        type="button"
        width="full"
        onClick={onDateTriggerClick}
      >
        <Inline space="xsmall" alignY="center">
          <IconDate tone="secondary" />
          <Text tone="secondary" truncate>
            {formatDateText(endDate, getLocale())}
          </Text>
        </Inline>
      </Box>
      <Box
        className={[styles.content, { [styles.isVisible]: showCalendar }]}
        zIndex="dropdown"
        ref={wrapperRef}
      >
        <Calendar
          currentLocalDate={selectedMonthDate}
          deactivateStartDate={true}
          maxDate={max}
          minDate={now}
          onDateButtonClicked={onDateClick}
          onMonthClicked={onMonthChange}
          value={{
            endDate: end,
            startDate: now,
          }}
        />
      </Box>
    </Box>
  );
};
